import React, { useCallback, useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { listAgentAnalysisReports } from '../../api/agentAnalysisReports';
import AgentSearchSelect from '../../components/form/AgentSearchSelect';
import EmptyState from '../../components/ui/EmptyState';
import { getApiErrorMessage } from '../../utils/errors';
import { formatPeriodKey, formatUpdatedAt, normalizeAgentIdParam } from './utils';

const PER_PAGE = 20;

function AgentAnalysisReportsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const agentId = normalizeAgentIdParam(searchParams.get('agentId'));
  const periodKey = searchParams.get('periodKey') || '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const updateFilters = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      Object.entries(next).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      if (!Object.prototype.hasOwnProperty.call(next, 'page')) params.delete('page');
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listAgentAnalysisReports({
        page,
        perPage: PER_PAGE,
        agentId: agentId || null,
        periodKey: periodKey || null,
      });
      setItems(response.data || []);
      setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      setItems([]);
      setError(getApiErrorMessage(err, 'Não foi possível carregar os relatórios.'));
    } finally {
      setLoading(false);
    }
  }, [agentId, page, periodKey]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Análises</h1>
        <p className="text-content-muted text-sm mt-1">
          Relatórios produzidos pelas automações dos seus agentes.
        </p>
      </div>

      <div className="surface-card p-4 grid gap-4 sm:grid-cols-2">
        <AgentSearchSelect
          id="analysis-report-agent"
          value={agentId}
          onChange={(event) => updateFilters({ agentId: event.target.value })}
          showAllOption
          layout="stacked"
          emptyLabel="Todos os agentes"
        />
        <label className="block">
          <span className="block text-sm font-medium text-content-secondary mb-1.5">
            Período
          </span>
          <input
            type="date"
            className="field-input"
            value={periodKey}
            onChange={(event) => updateFilters({ periodKey: event.target.value })}
          />
        </label>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
          <button type="button" onClick={loadReports} className="btn-secondary mt-3 text-sm px-3 py-2">
            Tentar novamente
          </button>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center text-content-muted">
          Carregando relatórios...
        </div>
      ) : !error && items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Nenhum relatório encontrado"
          description="A automação ainda não publicou um relatório para estes filtros."
        />
      ) : (
        <div className="space-y-3">
          {items.map((report) => (
            <Link
              key={report.id}
              to={`/analysis-reports/${report.id}`}
              className="surface-card flex items-center justify-between gap-4 p-5 hover:border-forge-300 hover:shadow-card-md transition-all"
            >
              <div className="min-w-0">
                <p className="font-semibold text-content-primary truncate">
                  {report.agent_name}
                </p>
                <p className="text-sm text-content-secondary mt-1">
                  {formatPeriodKey(report.period_key)}
                </p>
                <p className="text-xs text-content-muted mt-1">
                  Atualizado em {formatUpdatedAt(report.updated_at)}
                </p>
              </div>
              <span className="text-sm font-medium text-forge-600 shrink-0">
                Ver relatório
              </span>
            </Link>
          ))}
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="btn-secondary text-sm px-4 py-2"
            disabled={page <= 1}
            onClick={() => updateFilters({ page: String(page - 1) })}
          >
            Anterior
          </button>
          <span className="text-sm text-content-muted">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn-secondary text-sm px-4 py-2"
            disabled={page >= totalPages}
            onClick={() => updateFilters({ page: String(page + 1) })}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export default AgentAnalysisReportsList;
